/**
 * F1b de «Personalizar informes» (29-09-2026): el logo y los colores de la marca en
 * el .xlsx. SheetJS (la versión libre) no sabe de imágenes ni de estilos, así que se
 * edita el XML del fichero YA escrito, con fflate, como hace `documentos/rellenar.ts`:
 *
 *  - el logo, arriba a la derecha y alineado con el borde derecho de la tabla (encima
 *    de las filas del título, que a la derecha están vacías), con su proporción;
 *  - el título en negrita grande, la empresa en negrita y las líneas en gris pequeño;
 *  - la fila de cabecera de la tabla con el relleno suave del acento y su raya debajo;
 *  - la hoja ajustada a UNA página de ancho, en horizontal, para imprimir.
 *
 * Validado en el spike del 29-09 con openpyxl (sin avisos) y LibreOffice. Que Excel no
 * pida «reparar» no lo caza ningún test: se abre a mano en Excel y Numbers (F1b).
 * Si CUALQUIER cosa falla, se devuelve el fichero tal cual: el Excel sale sin estilos
 * pero con sus datos y sus líneas de cabecera (texto). Nunca se rompe una descarga.
 */
import { strFromU8, strToU8, unzipSync, zipSync } from "fflate";

/**
 * Bytes SIEMPRE de este contexto: en jsdom, TextEncoder da un Uint8Array de otro
 * «realm» y fflate arma un zip roto (la misma trampa que `documentos/rellenar.ts`).
 */
const u8 = (s: string) => new Uint8Array(strToU8(s));
import type { RGB } from "@/lib/pdf/brand";
import { coloresMarca } from "./paleta";
import type { MarcaInformes } from "./tipos";

export interface DisposicionHoja {
  /** Filas (0-based) de la cabecera del informe. */
  filaTitulo: number;
  filaEmpresa: number | null;
  filasLineas: number[];
  /** Fila (0-based) de la cabecera de la tabla y cuántas columnas tiene. */
  filaCabecera: number;
  columnas: number;
  /** Anchos de columna en caracteres (los de `!cols`). */
  anchos: number[];
}

const HOJA = "xl/worksheets/sheet1.xml";
const EMU_PX = 9525; // 1 px a 96 ppp
const LOGO_ALTO_PX = 44;
const LOGO_ANCHO_MAX_PX = 200;

/** A, B, …, Z, AA, AB… */
export function letraColumna(n: number): string {
  let s = "";
  let x = n + 1;
  while (x > 0) {
    const r = (x - 1) % 26;
    s = String.fromCharCode(65 + r) + s;
    x = Math.floor((x - 1) / 26);
  }
  return s;
}

const argb = (c: RGB) => `FF${c.map((v) => Math.round(v).toString(16).padStart(2, "0")).join("").toUpperCase()}`;

function bytesDeDataUrl(dataUrl: string): Uint8Array | null {
  const m = /^data:image\/png;base64,(.+)$/.exec(dataUrl);
  if (!m) return null;
  const bin = atob(m[1]);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/** Pone `s="…"` a una celda (quitando el que tuviera), sea `<c …>` o `<c …/>`. */
function conEstilo(xml: string, ref: string, s: number): string {
  const re = new RegExp(`<c r="${ref}"([^>]*?)(/?)>`);
  return xml.replace(re, (_m, attrs: string, cierre: string) => `<c r="${ref}" s="${s}"${attrs.replace(/\s+s="\d+"/, "")}${cierre}>`);
}

/** Añade un elemento a una lista con `count` de styles.xml y devuelve su índice. */
function agregar(st: { xml: string }, tag: string, elemento: string): number {
  const m = new RegExp(`<${tag} count="(\\d+)"`).exec(st.xml);
  if (!m) throw new Error(`styles.xml sin <${tag} count>`);
  const n = Number(m[1]);
  st.xml = st.xml.replace(m[0], `<${tag} count="${n + 1}"`).replace(`</${tag}>`, `${elemento}</${tag}>`);
  return n;
}

/** El .xlsx con la marca; si algo sale mal, el MISMO fichero que entró. */
export function aplicarMarcaXlsx(bytes: Uint8Array, marca: MarcaInformes, d: DisposicionHoja): Uint8Array {
  try {
    const z = unzipSync(new Uint8Array(bytes));
    if (!z[HOJA] || !z["xl/styles.xml"] || !z["[Content_Types].xml"]) return bytes;
    let hoja = strFromU8(z[HOJA]);
    const col = coloresMarca(marca);
    const TINTA = argb([15, 23, 42]);
    const SUAVE = argb([100, 116, 139]);

    // ── Estilos ──
    const st = { xml: strFromU8(z["xl/styles.xml"]) };
    const fuente = (b: boolean, sz: number, color: string) =>
      `<font>${b ? "<b/>" : ""}<sz val="${sz}"/><color rgb="${color}"/><name val="Calibri"/><family val="2"/></font>`;
    const fTitulo = agregar(st, "fonts", fuente(true, 14, TINTA));
    const fEmpresa = agregar(st, "fonts", fuente(true, 11, argb(col.principalTexto)));
    const fLinea = agregar(st, "fonts", fuente(false, 9, SUAVE));
    const fCab = agregar(st, "fonts", fuente(true, 10, TINTA));
    const relleno = agregar(st, "fills", `<fill><patternFill patternType="solid"><fgColor rgb="${argb(col.acentoSuave)}"/><bgColor indexed="64"/></patternFill></fill>`);
    const borde = agregar(st, "borders", `<border><left/><right/><top/><bottom style="medium"><color rgb="${argb(col.acento)}"/></bottom><diagonal/></border>`);
    const xf = (font: number, fill = 0, border = 0) =>
      `<xf numFmtId="0" fontId="${font}" fillId="${fill}" borderId="${border}" xfId="0" applyFont="1"${fill ? ' applyFill="1"' : ""}${border ? ' applyBorder="1"' : ""}/>`;
    const sTitulo = agregar(st, "cellXfs", xf(fTitulo));
    const sEmpresa = agregar(st, "cellXfs", xf(fEmpresa));
    const sLinea = agregar(st, "cellXfs", xf(fLinea));
    const sCab = agregar(st, "cellXfs", xf(fCab, relleno, borde));
    z["xl/styles.xml"] = u8(st.xml);

    hoja = conEstilo(hoja, `A${d.filaTitulo + 1}`, sTitulo);
    if (d.filaEmpresa != null) hoja = conEstilo(hoja, `A${d.filaEmpresa + 1}`, sEmpresa);
    for (const f of d.filasLineas) hoja = conEstilo(hoja, `A${f + 1}`, sLinea);
    for (let c = 0; c < d.columnas; c++) hoja = conEstilo(hoja, `${letraColumna(c)}${d.filaCabecera + 1}`, sCab);

    // ── Para imprimir: una página de ancho, en horizontal ──
    if (!/<sheetPr[\s>/]/.test(hoja)) hoja = hoja.replace(/(<worksheet[^>]*>)/, '$1<sheetPr><pageSetUpPr fitToPage="1"/></sheetPr>');
    if (!/<pageSetup[\s/>]/.test(hoja)) {
      const setup = '<pageSetup orientation="landscape" fitToWidth="1" fitToHeight="0"/>';
      const margenes = hoja.indexOf("<pageMargins");
      if (margenes >= 0) {
        const fin = hoja.indexOf("/>", margenes) + 2;
        hoja = hoja.slice(0, fin) + setup + hoja.slice(fin);
      } else {
        // Sin márgenes (SheetJS no siempre los escribe): van los dos, ANTES de lo que el esquema pone detrás.
        const detras = ["<headerFooter", "<rowBreaks", "<colBreaks", "<customProperties", "<cellWatches", "<ignoredErrors", "<smartTags", "<drawing", "<legacyDrawing", "<tableParts", "<extLst"];
        const pos = Math.min(...detras.map((t) => hoja.indexOf(t)).filter((i) => i >= 0), hoja.indexOf("</worksheet>"));
        hoja = `${hoja.slice(0, pos)}<pageMargins left="0.5" right="0.5" top="0.6" bottom="0.6" header="0.3" footer="0.3"/>${setup}${hoja.slice(pos)}`;
      }
    }

    // ── El logo ──
    const png = marca.logo ? bytesDeDataUrl(marca.logo.dataUrl) : null;
    if (png && marca.logo && marca.logo.w > 0 && marca.logo.h > 0) {
      const anchoPx = Math.min(LOGO_ANCHO_MAX_PX, Math.round((LOGO_ALTO_PX * marca.logo.w) / marca.logo.h));
      const altoPx = Math.round((anchoPx * marca.logo.h) / marca.logo.w);
      // Alineado al borde derecho de la tabla: la columna desde la que cabe, y el resto como desplazamiento.
      const anchosPx = Array.from({ length: Math.max(1, d.columnas) }, (_, i) => Math.round((d.anchos[i] ?? 10) * 7 + 5));
      let c = anchosPx.length - 1;
      let sobra = anchosPx[c] - anchoPx;
      while (sobra < 0 && c > 0) {
        c--;
        sobra += anchosPx[c];
      }
      const cx = anchoPx * EMU_PX;
      const cy = altoPx * EMU_PX;
      z["xl/media/marca-logo.png"] = png as Uint8Array<ArrayBuffer>;
      z["xl/drawings/drawing1.xml"] = u8(
        `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n<xdr:wsDr xmlns:xdr="http://schemas.openxmlformats.org/drawingml/2006/spreadsheetDrawing" xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><xdr:oneCellAnchor><xdr:from><xdr:col>${c}</xdr:col><xdr:colOff>${Math.max(0, sobra) * EMU_PX}</xdr:colOff><xdr:row>0</xdr:row><xdr:rowOff>0</xdr:rowOff></xdr:from><xdr:ext cx="${cx}" cy="${cy}"/><xdr:pic><xdr:nvPicPr><xdr:cNvPr id="2" name="Logo"/><xdr:cNvPicPr><a:picLocks noChangeAspect="1"/></xdr:cNvPicPr></xdr:nvPicPr><xdr:blipFill><a:blip r:embed="rId1"/><a:stretch><a:fillRect/></a:stretch></xdr:blipFill><xdr:spPr><a:xfrm><a:off x="0" y="0"/><a:ext cx="${cx}" cy="${cy}"/></a:xfrm><a:prstGeom prst="rect"><a:avLst/></a:prstGeom></xdr:spPr></xdr:pic><xdr:clientData/></xdr:oneCellAnchor></xdr:wsDr>`,
      );
      z["xl/drawings/_rels/drawing1.xml.rels"] = u8(
        `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/image" Target="../media/marca-logo.png"/></Relationships>`,
      );
      const relsHoja = "xl/worksheets/_rels/sheet1.xml.rels";
      const rel = `<Relationship Id="rIdMarca1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/drawing" Target="../drawings/drawing1.xml"/>`;
      z[relsHoja] = u8(
        z[relsHoja]
          ? strFromU8(z[relsHoja]).replace("</Relationships>", `${rel}</Relationships>`)
          : `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">${rel}</Relationships>`,
      );
      if (!/xmlns:r=/.test(hoja.slice(0, hoja.indexOf(">", hoja.indexOf("<worksheet")) + 1))) {
        hoja = hoja.replace("<worksheet ", '<worksheet xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships" ');
      }
      // <drawing> va DESPUÉS de pageMargins/pageSetup/… y ANTES de legacyDrawing/tableParts/extLst (orden del esquema).
      const despues = ["<legacyDrawing", "<legacyDrawingHF", "<drawingHF", "<picture", "<oleObjects", "<controls", "<webPublishItems", "<tableParts", "<extLst"];
      const pos = Math.min(...despues.map((t) => hoja.indexOf(t)).filter((i) => i >= 0), hoja.indexOf("</worksheet>"));
      hoja = `${hoja.slice(0, pos)}<drawing r:id="rIdMarca1"/>${hoja.slice(pos)}`;

      let tipos = strFromU8(z["[Content_Types].xml"]);
      if (!/Extension="png"/i.test(tipos)) tipos = tipos.replace("<Override", '<Default Extension="png" ContentType="image/png"/><Override');
      tipos = tipos.replace("</Types>", '<Override PartName="/xl/drawings/drawing1.xml" ContentType="application/vnd.openxmlformats-officedocument.drawing+xml"/></Types>');
      z["[Content_Types].xml"] = u8(tipos);
    }

    z[HOJA] = u8(hoja);
    return new Uint8Array(zipSync(z, { level: 6 }));
  } catch {
    return bytes;
  }
}
